// Copyright (c) 2025 Just Feasible Solutions LLC. All rights reserved.
// This file is part of the Just Feasible Solutions application.
// Unauthorized copying, distribution, or modification of this file is prohibited.
// For license information, contact the author.

import React, { useState } from 'react';
import { BrowserRouter as Router, Routes, Route, useNavigate } from 'react-router-dom';
import Privacy from './privacy';
import LegalDisclosure from './legalDisclosure';
import SecurityPage from './securityPage';
import Accessibility from './accessibility';
import SocialMediaDisclaimer from './socialmediadisclaimer';
import PrivacyChoices from './privacychoices';
import CookiesSetting from './cookiesSetting';
import ContactUs from './ContactUs';
import CompanyInfo from './CompanyInfo';
import BkIncome from './bkeeping/bkIncome';
import BkTransaction from './bkeeping/bkTransaction';
import BkReport from './bkeeping/bkReport';
import BkAnnualReport from './bkeeping/bkAnnualReport';
import './App.css';

function App() {
  const [page, setPage] = useState(() => {
    if (window.location.pathname === '/verify-email') return 'verify-email';
    return 'home';
  });
  const [bookPage, setBookPage] = useState('overview');
  const [footerModal, setFooterModal] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  function NavWrapper({ children }) {
    const navigate = useNavigate();
    const handleNav = (e, target, path = null) => {
      e.preventDefault();
      setPage(target);
      setMobileMenuOpen(false);
      if (target === 'bookkeeping') {
        setBookPage('overview');
        window.location.hash = '#products#bookkeeping';
      }
      if (path) {
        navigate(path);
      } else if (target === 'home') {
        navigate('/');
      }
    };
    const handleRouteNav = (target, path) => {
      setPage(target);
      setFooterModal(null);
      navigate(path);
    };
    return children({ handleNav, handleRouteNav });
  }

  return (
    <Router>
      <NavWrapper>
        {({ handleNav, handleRouteNav }) => (
          <div className="app-outer">
            {/* Top Navigation Bar */}
            <nav className="top-nav">
              {/* Left: Logo */}
              <div className="nav-logo" onClick={() => { setPage('home'); window.location.hash = ''; handleNav({ preventDefault: () => {} }, 'home', '/'); }}>
                <img src={process.env.PUBLIC_URL + '/logo.png'} alt="Logo" />
              </div>
              {/* Hamburger Icon */}
              <button className="nav-hamburger" aria-label="Open menu" onClick={() => setMobileMenuOpen(m => !m)}>
                <span />
                <span />
                <span />
              </button>
              {/* Menu & Overlay */}
              {mobileMenuOpen && (
                <div className="nav-menu-overlay" onClick={() => setMobileMenuOpen(false)} />
              )}
              <div className={`nav-menu${mobileMenuOpen ? ' open' : ''}`}> 
                <a href="#services" onClick={e => handleNav(e, 'home', '/')}>Services</a>
                <div className="nav-dropdown">
                  <a href="#products" tabIndex={0}>Products &#x25BE;</a>
                  <div className="dropdown-content">
                    <a href="#bookkeeping" onClick={e => handleNav(e, 'bookkeeping', null)}>Book Keeping</a>
                    <a href="#locationhistory">Location History</a>
                    <a href="#jfsreceiptvault">jfsReceiptVault</a>
                  </div>
                </div>
                <a href="#industries" onClick={e => handleNav(e, 'home', '/')}>Industries</a>
                <div className="nav-dropdown">
                  <a href="#about" tabIndex={0}>About &#x25BE;</a>
                  <div className="dropdown-content">
                    <a href="#companyinfo" onClick={e => handleNav(e, 'companyinfo', '/companyinfo')}>Company Info</a>
                  </div>
                </div>
                <a href="#contact" className="nav-contact" onClick={e => handleNav(e, 'contact', '/')}>Contact Us</a>
              </div>
            </nav>
            {/* Main Content */}
            <main className="app-main">
              <Routes>
                <Route path="/" element={
                  <>
                    {/* Home/Services Page */}
                    <section className="hero" style={{ padding: '4rem 1rem', background: '#f5f7fa', textAlign: 'center' }}>
                      <h1 style={{ fontSize: '2.5rem', marginBottom: '1rem' }}>
                        Building Software to <span style={{ color: '#0070f3' }}>Solve, Scale, and Succeed.</span>
                      </h1>
                      <p style={{ fontSize: '1.25rem', maxWidth: 600, margin: '0 auto 2rem' }}>
                        Whether you’re building your vision or transforming your operations, we deliver custom software solutions tailored to your goals.
                      </p>
                      <div style={{ display: 'flex', justifyContent: 'center', gap: '1rem' }}>
                        <a href="#contact" style={{ padding: '0.75rem 2rem', background: '#0070f3', color: '#fff', borderRadius: 6, textDecoration: 'none', fontWeight: 600 }}>Get started</a>
                        <a href="#services" style={{ padding: '0.75rem 2rem', background: '#fff', color: '#0070f3', border: '2px solid #0070f3', borderRadius: 6, textDecoration: 'none', fontWeight: 600 }}>Explore Our Work</a>
                      </div>
                    </section>
                    <section id="services" style={{ padding: '3rem 1rem', background: '#fff' }}>
                      <h2 style={{ textAlign: 'center', fontSize: '2rem', marginBottom: '2rem' }}>Services We Provide</h2>
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '2rem', maxWidth: 1100, margin: '0 auto' }}>
                        {[
                          { title: 'APIs', desc: '' },
                          { title: 'Project management', desc: '' },
                          { title: 'Web Development', desc: '' },
                          { title: 'Mobile Development', desc: '' },
                          { title: 'Technical Consulting', desc: '' },
                          { title: 'Artificial Intelligence', desc: '' },
                        ].map((service, i) => (
                          <div key={i} style={{ background: '#f5f7fa', borderRadius: 10, padding: '2rem 1rem', boxShadow: '0 2px 8px rgba(0,0,0,0.04)', textAlign: 'center' }}>
                            <div style={{ fontSize: '2.5rem', marginBottom: '1rem' }}>🔷</div>
                            <h3 style={{ margin: '0 0 0.5rem' }}>{service.title}</h3>
                            <p style={{ margin: 0, color: '#555' }}>{service.desc}</p>
                          </div>
                        ))}
                      </div>
                    </section>
                  </>
                } />
                <Route path="/companyinfo" element={<CompanyInfo />} />
                <Route path="/privacy" element={<Privacy />} />
                <Route path="/legal" element={<LegalDisclosure />} />
                <Route path="/security" element={<SecurityPage />} />
                <Route path="/accessibility" element={<Accessibility />} />
                <Route path="/social" element={<SocialMediaDisclaimer />} />
                <Route path="/privacychoices" element={<PrivacyChoices />} />
                <Route path="/cookies" element={<CookiesSetting />} />
              </Routes>
              {/* Existing state-based navigation and content below */}
              {page === 'contact' && (
                <ContactUs />
              )}
              {page === 'verify-email' && (
                <React.Suspense fallback={<div>Loading...</div>}>
                  {React.createElement(require('./VerifyEmail').default)}
                </React.Suspense>
              )}
              {page === 'admin' && (
                <React.Suspense fallback={<div>Loading...</div>}>
                  {React.createElement(require('./about/jfsAdmin').default)}
                </React.Suspense>
              )}
              {page === 'bookkeeping' && (
                <section style={{display: 'flex', background: '#f5f7fa'}}>
                  {/* ...existing bookkeeping code... */}
                </section>
              )}
            </main>
            {/* Copyright Disclosure */}
            <footer className="app-footer">
              <div style={{ marginBottom: 8 }}>
                &copy; 2025 Just Feasible Solutions LLC. All rights reserved. This application is part of the Just Feasible Solutions suite. Unauthorized copying, distribution, or modification is prohibited.
              </div>
              <nav style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: '1.5rem', marginBottom: 6 }}>
                <button type="button" onClick={() => handleRouteNav('privacy', '/privacy')} style={{ color: '#1976d2', textDecoration: 'none', fontWeight: 500, background: 'none', border: 'none', padding: 0, cursor: 'pointer', font: 'inherit' }}>Privacy Policy</button>
                <button type="button" onClick={() => handleRouteNav('legal', '/legal')} style={{ color: '#1976d2', textDecoration: 'none', fontWeight: 500, background: 'none', border: 'none', padding: 0, cursor: 'pointer', font: 'inherit' }}>Legal Disclosures</button>
                <button type="button" onClick={() => handleRouteNav('security', '/security')} style={{ color: '#1976d2', textDecoration: 'none', fontWeight: 500, background: 'none', border: 'none', padding: 0, cursor: 'pointer', font: 'inherit' }}>Security</button>
                <button type="button" onClick={() => setFooterModal('licensing')} style={{ color: '#1976d2', textDecoration: 'none', fontWeight: 500, background: 'none', border: 'none', padding: 0, cursor: 'pointer', font: 'inherit' }}>Licensing</button>
                <button type="button" onClick={() => handleRouteNav('accessibility', '/accessibility')} style={{ color: '#1976d2', textDecoration: 'none', fontWeight: 500, background: 'none', border: 'none', padding: 0, cursor: 'pointer', font: 'inherit' }}>Accessibility</button>
                <button type="button" onClick={() => handleRouteNav('social', '/social')} style={{ color: '#1976d2', textDecoration: 'none', fontWeight: 500, background: 'none', border: 'none', padding: 0, cursor: 'pointer', font: 'inherit' }}>Social Media Disclaimer</button>
                <button type="button" onClick={() => handleRouteNav('privacyChoices', '/privacychoices')} style={{ color: '#1976d2', textDecoration: 'none', fontWeight: 500, background: 'none', border: 'none', padding: 0, cursor: 'pointer', font: 'inherit' }}>Privacy Choices</button>
                <button type="button" onClick={() => handleRouteNav('cookies', '/cookies')} style={{ color: '#1976d2', textDecoration: 'none', fontWeight: 500, background: 'none', border: 'none', padding: 0, cursor: 'pointer', font: 'inherit' }}>Cookies Settings</button>
              </nav>
              {/* Footer Modal Content */}
              {footerModal && (
                <div style={{ position: 'fixed', left: 0, top: 0, width: '100vw', height: '100vh', background: 'rgba(0,0,0,0.35)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={() => setFooterModal(null)}>
                  <div style={{ background: '#fff', borderRadius: 10, maxWidth: 480, width: '90%', padding: '2rem', boxShadow: '0 2px 16px rgba(0,0,0,0.15)', position: 'relative' }} onClick={e => e.stopPropagation()}>
                    <button onClick={() => setFooterModal(null)} style={{ position: 'absolute', top: 12, right: 16, background: 'none', border: 'none', fontSize: 22, color: '#888', cursor: 'pointer' }} aria-label="Close">&times;</button>
                    {footerModal === 'privacy' && (
                      <div>
                        <h3>Privacy Policy</h3>
                        <p>Your privacy is important to us. We do not sell or share your personal information with third parties except as required by law or to provide core services. Please review our full privacy policy for details.</p>
                      </div>
                    )}
                    {footerModal === 'legal' && (
                      <div>
                        <h3>Legal Disclosures</h3>
                        <p>This application is provided as-is without any warranties. Use is subject to applicable laws and regulations. Please consult our legal disclosures for more information.</p>
                      </div>
                    )}
                    {footerModal === 'security' && (
                      <div>
                        <h3>Security</h3>
                        <p>We use industry-standard security measures to protect your data. However, no system is completely secure. Please use strong passwords and keep your credentials confidential.</p>
                      </div>
                    )}
                    {footerModal === 'licensing' && (
                      <div>
                        <h3>Licensing</h3>
                        <p>This software is licensed to you by Just Feasible Solutions LLC. Unauthorized use, distribution, or modification is prohibited. For licensing inquiries, contact us directly.</p>
                      </div>
                    )}
                    {footerModal === 'accessibility' && (
                      <div>
                        <h3>Accessibility</h3>
                        <p>We strive to make our application accessible to all users. If you encounter accessibility issues, please contact us so we can improve your experience.</p>
                      </div>
                    )}
                    {footerModal === 'social' && (
                      <div>
                        <h3>Social Media Disclaimer</h3>
                        <p>References to social media do not imply endorsement. Content shared on social platforms is subject to their respective terms and policies.</p>
                      </div>
                    )}
                    {footerModal === 'privacyChoices' && (
                      <div>
                        <h3>Privacy Choices</h3>
                        <p>You have choices regarding your personal data. Manage your privacy settings in your account or contact us for assistance.</p>
                      </div>
                    )}
                    {footerModal === 'cookies' && (
                      <div>
                        <h3>Cookies Settings</h3>
                        <p>This application uses cookies to enhance your experience. You can manage your cookie preferences in your browser settings.</p>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </footer>
          </div>
        )}
      </NavWrapper>
    </Router>
  );
}

export default App;
